// Sentry client-side init. Imported once at the top of src/index.js so the
// SDK is armed BEFORE the React tree renders — otherwise early errors miss
// the beacon.
//
// DSN / org / project / auth-token all live in src/config.json (same
// convention as VIBESDK_SECRET / keyString elsewhere in this project). The
// auth token is only used at build time by @sentry/vite-plugin — the SDK
// itself needs only the DSN.

import * as Sentry from "@sentry/react";
import config from "./config.json";

const host = typeof window !== "undefined" ? window.location.host : "";
const environment =
  /localhost(:|$)|127\.0\.0\.1/i.test(host) ? "local" :
  /ai-editor72/i.test(host)                  ? "staging" :
  "production";

// Kill switch: "sentryEnabled": false in src/config.json stops all reporting.
// Sentry.init() still runs so Sentry.ErrorBoundary in src/index.js keeps
// working — the client just drops every event instead of sending it. Defaults
// to on when the key is missing.
const enabled = config.sentryEnabled !== false;

Sentry.init({
  dsn: config.sentryDsn,
  enabled,
  environment,
  // Release is the environment itself — no per-build version tracking. This
  // means every "production" deploy overwrites the previous release in Sentry;
  // source maps for the current bundle always live under this name.
  release: environment,

  // Left out entirely when disabled — Replay buffers DOM mutations in memory
  // whether or not events ship, so `enabled: false` alone would keep paying
  // that cost for nothing.
  integrations: enabled ? [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })
  ] : [],

  // 10% transaction sampling in prod, everything in staging/local.
  tracesSampleRate: environment === "production" ? 0.1 : 1.0,

  // Session Replay: never a full session by default; grab the last ~60s on
  // any captured error.
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,

  sendDefaultPii: false,

  // Silence pre-existing React 18 / project-specific console warnings so
  // Sentry doesn't drown in known noise.
  ignoreErrors: [
    /findDOMNode is deprecated/,
    /ReactDOM.render is no longer supported/,
    /Received `.*` for a non-boolean attribute/,
    /Each child in a list should have a unique "key"/
  ],

  beforeSend(event) {
    // Strip vibesdk tokens from URLs — they encrypt userId/email but there's
    // no need to send them to Sentry either way.
    if (event.request && event.request.url) {
      event.request.url = event.request.url.replace(/token=[^&]+/g, "token=REDACTED");
    }
    return event;
  }
});

// Tag once we have identity — safe to call multiple times; Sentry merges.
export function setSentryUser() {
  if (typeof window === "undefined") return;
  try {
    Sentry.setUser({
      id: localStorage.getItem("userId") || undefined,
      email: localStorage.getItem("userEmail") || undefined
    });
    Sentry.setTag("aiAppId", localStorage.getItem("aiAppId") || "");
    Sentry.setTag("aiPageId", localStorage.getItem("aiPageId") || "");
  } catch (_) { /* localStorage disabled — ignore */ }
}

// Call once at boot; app.js can call again after login state changes.
setSentryUser();

export { Sentry };
